import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderKanban,
  HardDrive,
  Loader2,
  Presentation,
  Search,
  Share2,
  X,
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { authAPI, organisationsAPI } from '../../services/api';
import toast from '../../services/toast';

/** @typedef {'myDrive'|'sharedDrive'|'sharedWithMe'} BrowseLocation */

const LOCATION_NAV = [
  { id: /** @type {BrowseLocation} */ ('myDrive'), label: 'My Drive', Icon: HardDrive },
  { id: /** @type {BrowseLocation} */ ('sharedDrive'), label: 'Shared drives', Icon: FolderKanban },
  { id: /** @type {BrowseLocation} */ ('sharedWithMe'), label: 'Shared with me', Icon: Share2 },
];

/**
 * @param {BrowseLocation} location
 * @param {string|null} [driveId]
 */
function rootCrumb(location, driveId = null) {
  const label = LOCATION_NAV.find((n) => n.id === location)?.label || 'My Drive';
  return { id: 'root', name: label, location, driveId };
}

function formatModifiedFull(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatModifiedRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffSec = Math.round((now - d.getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    if (Math.abs(diffSec) < 60) return rtf.format(-diffSec, 'second');
    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return rtf.format(-diffHr, 'hour');
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffDay) < 30) return rtf.format(-diffDay, 'day');
    return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return formatModifiedFull(iso);
  }
}

function itemKey(item) {
  return `${item.kind}:${item.id}`;
}

/**
 * @param {string} mimeType
 * @param {boolean} isFolder
 */
function DriveItemIcon({ mimeType, isFolder }: any) {
  if (isFolder) {
    return <Folder className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />;
  }
  const mime = String(mimeType || '');
  if (mime === 'application/vnd.google-apps.document') {
    return <FileText className="h-5 w-5 shrink-0 text-blue-500" aria-hidden />;
  }
  if (mime === 'application/vnd.google-apps.spreadsheet') {
    return <FileSpreadsheet className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />;
  }
  if (mime === 'application/vnd.google-apps.presentation') {
    return <Presentation className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />;
  }
  return <FileText className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />;
}

/**
 * @param {BrowseLocation} location
 * @param {string} [searchQuery]
 */
function emptyMessageForLocation(location, searchQuery) {
  if (searchQuery) return `No results for "${searchQuery}".`;
  if (location === 'sharedDrive') return 'No shared drives available.';
  if (location === 'sharedWithMe') return 'Nothing has been shared with you.';
  return 'This folder is empty.';
}

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   orgId: string,
 *   streamId: string,
 *   projectId: string,
 *   repositoryId?: string|null,
 *   onImported?: () => void|Promise<void>,
 *   onImportStarted?: (info: { itemCount: number }) => void,
 * }} props
 */
export default function GoogleDriveBrowseModal({
  isOpen,
  onClose,
  orgId,
  streamId,
  projectId,
  repositoryId = null,
  onImported,
  onImportStarted,
}: any) {
  const [driveStatus, setDriveStatus] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState(() => [rootCrumb('myDrive')]);
  const [items, setItems] = useState<any[]>([]);
  const [nextPageToken, setNextPageToken] = useState<any>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<any>(null);
  const [selected, setSelected] = useState(() => new Map());
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const tailCrumb = breadcrumbs[breadcrumbs.length - 1] || rootCrumb('myDrive');
  const browseLocation = tailCrumb.location || 'myDrive';
  const browseDriveId = tailCrumb.driveId || null;
  const parentId = tailCrumb.id || 'root';
  const locationLabel = LOCATION_NAV.find((n) => n.id === browseLocation)?.label || 'My Drive';
  const isSearching = searchQuery.length >= 2;
  const connected = driveStatus?.connection?.connected === true;
  const connectionEmail = driveStatus?.connection?.googleEmail || null;

  const resetBrowseState = useCallback(() => {
    setBreadcrumbs([rootCrumb('myDrive')]);
    setItems([]);
    setNextPageToken(null);
    setListError(null);
    setSelected(new Map());
    setSearchInput('');
    setSearchQuery('');
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const data = await authAPI.getGoogleDriveStatus();
      setDriveStatus(data);
    } catch (e) {
      setDriveStatus(null);
      toast.error((e as Error).message || 'Failed to load Google Drive status');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchPage = useCallback(
    async ({ crumb, pageToken, append, search }) => {
      if (!connected || !crumb) return;
      setListLoading(true);
      setListError(null);
      try {
        const data = await authAPI.browseGoogleDrive({
          location: crumb.location || 'myDrive',
          parentId: crumb.id || 'root',
          driveId: crumb.driveId || undefined,
          search: search || undefined,
          pageToken,
        });
        const pageItems = Array.isArray(data.items) ? data.items : [];
        setItems((prev) => (append ? [...prev, ...pageItems] : pageItems));
        setNextPageToken(data.nextPageToken || null);
      } catch (e) {
        setListError((e as Error).message || 'Could not list Drive folder');
        if (!append) setItems([]);
      } finally {
        setListLoading(false);
      }
    },
    [connected]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchQuery(trimmed.length >= 2 ? trimmed : '');
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen) return;
    resetBrowseState();
    loadStatus();
  }, [isOpen, loadStatus, resetBrowseState]);

  useEffect(() => {
    if (!isOpen || !connected) return;
    fetchPage({ crumb: tailCrumb, pageToken: null, append: false, search: searchQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when browse path or search changes
  }, [isOpen, connected, browseLocation, browseDriveId, parentId, searchQuery, fetchPage]);

  const selectedList = useMemo(() => Array.from(selected.values()), [selected]);
  const selectedCount = selectedList.length;

  const ingestibleOnPage = useMemo(
    () => items.filter((row) => row.isFolder || row.ingestible),
    [items]
  );

  const switchLocation = (location) => {
    setBreadcrumbs([rootCrumb(location)]);
    setSelected(new Map());
    setSearchInput('');
    setSearchQuery('');
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const toggleRow = (row) => {
    if (!row.isFolder && !row.ingestible) return;
    const key = itemKey({ id: row.id, kind: row.isFolder ? 'folder' : 'file' });
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else {
        next.set(key, {
          driveId: row.id,
          kind: row.isFolder ? 'folder' : 'file',
          name: row.name,
          mimeType: row.mimeType,
        });
      }
      return next;
    });
  };

  const selectIngestibleOnPage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const row of ingestibleOnPage) {
        const key = itemKey({ id: row.id, kind: row.isFolder ? 'folder' : 'file' });
        next.set(key, {
          driveId: row.id,
          kind: row.isFolder ? 'folder' : 'file',
          name: row.name,
          mimeType: row.mimeType,
        });
      }
      return next;
    });
  };

  const navigateToCrumb = (index) => {
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSelected(new Map());
  };

  const openFolder = (row) => {
    if (!row.isFolder) return;
    clearSearch();
    if (row.isSharedDriveRoot) {
      setBreadcrumbs([
        rootCrumb('sharedDrive'),
        { id: row.id, name: row.name, location: 'sharedDrive', driveId: row.id },
      ]);
    } else if (row.driveId && browseLocation === 'myDrive' && !browseDriveId) {
      setBreadcrumbs([
        rootCrumb('sharedDrive'),
        { id: row.id, name: row.name, location: 'sharedDrive', driveId: row.driveId },
      ]);
    } else {
      setBreadcrumbs((prev) => [
        ...prev,
        {
          id: row.id,
          name: row.name,
          location: browseLocation,
          driveId: browseDriveId || row.driveId || null,
        },
      ]);
    }
    setSelected(new Map());
  };

  const handleClose = () => {
    resetBrowseState();
    onClose();
  };

  const handleImport = () => {
    if (!orgId || !streamId || !projectId || selectedCount === 0) return;

    const payloadItems = selectedList.map(({ driveId, kind, name, mimeType }) => ({
      driveId,
      kind,
      name,
      mimeType,
    }));
    const count = selectedCount;

    resetBrowseState();
    onClose();
    onImportStarted?.({ itemCount: count });
    toast.info(
      count === 1
        ? 'Adding 1 item from Google Drive in the background…'
        : `Adding ${count} items from Google Drive in the background…`
    );

    void organisationsAPI
      .importGoogleDriveToProject(orgId, {
        streamId,
        projectId,
        repositoryId: repositoryId || undefined,
        items: payloadItems,
      })
      .then(async (result) => {
        if (result?.accepted) {
          if (onImported) await onImported();
          return;
        }
        const imported = result?.imported?.length ?? 0;
        const skipped = result?.skipped?.length ?? 0;
        const failed = result?.failed?.length ?? 0;
        const parts: string[] = [];
        if (imported) parts.push(`${imported} added`);
        if (skipped) parts.push(`${skipped} skipped`);
        if (failed) parts.push(`${failed} failed`);
        toast.success(parts.length ? parts.join(', ') : 'Import completed');
        if (onImported) await onImported();
      })
      .catch((e) => {
        toast.error((e as Error).message || 'Could not start Google Drive import');
      });
  };

  const ownerDisplay = (row) => {
    if (row.ownerName) return row.ownerName;
    if (row.locationHint) return row.locationHint;
    if (connectionEmail && browseLocation === 'myDrive') return 'me';
    return '—';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add from Google Drive"
      icon={<HardDrive className="h-5 w-5 text-vivid-red" aria-hidden />}
      contentClassName="max-w-[min(1400px,calc(100vw-3rem))] w-[calc(100vw-3rem)] max-h-[90vh] overflow-hidden"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0 pt-0"
    >
      {connectionEmail ? (
        <p className="shrink-0 border-b border-dark-border px-6 py-2 text-xs text-slate-500">
          Connected as {connectionEmail}
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {statusLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 py-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Checking connection…
          </div>
        ) : null}

        {!statusLoading && !connected ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center text-sm text-slate-400">
            <p className="mb-4">
              Connect your Google account in Account settings to browse files here.
            </p>
            <Link
              to="/settings/account/integrations"
              className="btn-primary-mdl inline-flex text-sm"
              onClick={handleClose}
            >
              Connect Google Drive
            </Link>
          </div>
        ) : null}

        {!statusLoading && connected ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
            <nav
              className="shrink-0 border-b border-dark-border bg-dark-base px-2 py-2 md:w-[220px] md:border-b-0 md:border-r md:py-4"
              aria-label="Google Drive locations"
            >
              <ul className="flex gap-1 md:flex-col md:gap-0.5">
                {LOCATION_NAV.map(({ id, label, Icon }) => {
                  const active = browseLocation === id;
                  return (
                    <li key={id} className="flex-1 md:flex-none">
                      <button
                        type="button"
                        className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                          active
                            ? 'bg-dark-surface font-medium text-slate-100 shadow-sm'
                            : 'text-slate-400 hover:bg-dark-surface/80/50'
                        }`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => switchLocation(id)}
                        disabled={listLoading && active}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="truncate">{label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-4 py-3 md:px-5">
              {isSearching ? (
                <p className="mb-2 shrink-0 text-sm text-slate-400">
                  Search results in{' '}
                  <span className="font-medium text-slate-100">{locationLabel}</span>
                  {parentId !== 'root' ? (
                    <>
                      {' '}
                      · <span className="font-medium">{tailCrumb.name}</span>
                    </>
                  ) : null}{' '}
                  for &ldquo;{searchQuery}&rdquo;
                </p>
              ) : (
                <nav
                  className="mb-2 flex shrink-0 flex-wrap items-center gap-1 text-sm"
                  aria-label="Drive folder path"
                >
                  {breadcrumbs.map((crumb, idx) => (
                    <span
                      key={`${crumb.location}-${crumb.id}-${idx}`}
                      className="inline-flex items-center gap-1"
                    >
                      {idx > 0 ? (
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                      ) : null}
                      <button
                        type="button"
                        className={
                          idx === breadcrumbs.length - 1
                            ? 'max-w-[200px] truncate font-medium text-slate-100'
                            : 'max-w-[160px] truncate text-vivid-red-light hover:underline'
                        }
                        title={crumb.name}
                        onClick={() => navigateToCrumb(idx)}
                        disabled={idx === breadcrumbs.length - 1 || listLoading}
                      >
                        {crumb.name}
                      </button>
                    </span>
                  ))}
                </nav>
              )}

              <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary-mdl text-xs"
                  onClick={selectIngestibleOnPage}
                  disabled={listLoading || ingestibleOnPage.length === 0}
                >
                  Select ingestible on this page
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl text-xs"
                  onClick={() => setSelected(new Map())}
                  disabled={selectedCount === 0}
                >
                  Clear selection
                </button>
                <label className="relative ml-auto flex min-w-0 flex-1 items-center sm:max-w-xs">
                  <Search
                    className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-slate-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search in this location"
                    className="w-full rounded-md border border-dark-border bg-dark-base py-1.5 pl-7 pr-7 text-xs text-slate-100"
                    aria-label="Search Google Drive"
                  />
                  {searchInput ? (
                    <button
                      type="button"
                      className="absolute right-1.5 rounded p-0.5 text-slate-400 hover:text-slate-400"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : null}
                </label>
                {searchInput.trim().length === 1 ? (
                  <span className="w-full text-xs text-slate-400 sm:w-auto">
                    Type at least 2 characters
                  </span>
                ) : null}
              </div>

              {listError ? (
                <p className="mb-2 shrink-0 rounded-md border-red-900/50 bg-red-950/30 p-2 text-red-200">
                  {listError}
                </p>
              ) : null}

              <div className="min-h-0 flex-1 overflow-auto rounded-md border border-dark-border">
                <table className="w-full table-fixed text-left text-sm">
                  <thead className="sticky top-0 z-[1] border-b border-dark-border bg-dark-base text-xs font-medium text-slate-400">
                    <tr>
                      <th className="w-10 px-3 py-2.5" scope="col">
                        <span className="sr-only">Select</span>
                      </th>
                      <th className="min-w-[12rem] px-3 py-2.5" scope="col">
                        Name
                      </th>
                      <th className="hidden w-40 px-3 py-2.5 sm:table-cell" scope="col">
                        Owner
                      </th>
                      <th className="hidden w-36 px-3 py-2.5 md:table-cell" scope="col">
                        Modified
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-dark-base">
                    {listLoading && items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-12 text-center text-slate-400">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-hidden />
                        </td>
                      </tr>
                    ) : null}
                    {!listLoading && items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-12 text-center text-slate-400">
                          {emptyMessageForLocation(browseLocation, searchQuery)}
                        </td>
                      </tr>
                    ) : null}
                    {items.map((row) => {
                      const key = itemKey({ id: row.id, kind: row.isFolder ? 'folder' : 'file' });
                      const checked = selected.has(key);
                      const checkboxDisabled = !row.isFolder && !row.ingestible;
                      const checkboxTitle =
                        !row.isFolder && !row.ingestible
                          ? row.ingestibleReason || 'Not supported for ingest'
                          : undefined;
                      const canOpen = row.isFolder;
                      return (
                        <tr key={row.id} className="h-10 hover:bg-dark-elevated/50">
                          <td className="px-3 align-middle">
                            <input
                              type="checkbox"
                              className="rounded border-dark-border"
                              checked={checked}
                              disabled={checkboxDisabled}
                              title={checkboxTitle}
                              aria-label={`Select ${row.name}`}
                              onChange={() => toggleRow(row)}
                            />
                          </td>
                          <td className="min-w-0 px-3 align-middle">
                            {canOpen ? (
                              <button
                                type="button"
                                className="inline-flex max-w-full items-center gap-2 text-left font-medium text-slate-100 hover:text-vivid-red-light"
                                title={row.name}
                                onClick={() => openFolder(row)}
                              >
                                <DriveItemIcon mimeType={row.mimeType} isFolder />
                                <span className="truncate">{row.name}</span>
                                <ChevronRight
                                  className="h-3.5 w-3.5 shrink-0 opacity-50"
                                  aria-hidden
                                />
                              </button>
                            ) : (
                              <span
                                className="inline-flex max-w-full items-center gap-2 text-slate-100"
                                title={row.name}
                              >
                                <DriveItemIcon mimeType={row.mimeType} isFolder={false} />
                                <span className="truncate">{row.name}</span>
                              </span>
                            )}
                          </td>
                          <td
                            className="hidden max-w-[10rem] truncate px-3 text-slate-400 sm:table-cell"
                            title={ownerDisplay(row)}
                          >
                            {ownerDisplay(row)}
                          </td>
                          <td
                            className="hidden px-3 text-slate-400 md:table-cell"
                            title={formatModifiedFull(row.modifiedTime)}
                          >
                            {formatModifiedRelative(row.modifiedTime)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {nextPageToken ? (
                <button
                  type="button"
                  className="btn-secondary-mdl mt-2 w-full shrink-0 text-sm"
                  disabled={listLoading}
                  onClick={() =>
                    fetchPage({
                      crumb: tailCrumb,
                      pageToken: nextPageToken,
                      append: true,
                      search: searchQuery,
                    })
                  }
                >
                  {listLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Loading…
                    </span>
                  ) : (
                    'Load more'
                  )}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {connected ? (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-dark-border bg-dark-base px-4 py-3 md:px-6">
          <span className="text-sm text-slate-400">
            {selectedCount === 0 ? 'No items selected' : `${selectedCount} selected`}
          </span>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary-mdl text-sm" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary-mdl text-sm disabled:opacity-50"
              disabled={selectedCount === 0}
              onClick={handleImport}
            >
              {`Add ${selectedCount} ${selectedCount === 1 ? 'item' : 'items'} to project`}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
